/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useMemo, useState } from 'react';
import { Box, HStack, Icon, IconButton, Link, Table, VStack } from 'chakra-v3';
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from 'react-icons/lu';
import { FaCheckCircle } from 'react-icons/fa';
import { Text } from '../../components/Text';
import humanEvalLeaderboardData from '../../data/human_eval_leaderboard.json';
import llmJudgeLeaderboardData from '../../data/llm_judge_leaderboard.json';
import Dropdown from './Dropdown';
import { MdOutlineOpenInNew, MdOutlineRadioButtonUnchecked } from "react-icons/md";


type SortDirection = 'asc' | 'desc' | null;
type EvaluationType = 'human' | 'llm';
type SortableMetric =
  | 'rank'
  | 'averageScore'
  | 'languageQuality'
  | 'contentQuality'
  | 'trustScore'
  | 'localRelevance'
  | 'glicko2Rating';

type MetricCell = {
  value: number | null;
  error: number | null;
};

type RawMetric = {
  value: number;
  error: number;
};

type RawModel = {
  id: string;
  name: string;
  rank: number;
  metrics: {
    languageQuality: RawMetric;
    contentQuality: RawMetric;
    trustworthiness: RawMetric;
    localRelevance: RawMetric;
  };
};

type LeaderboardRow = {
  id: string;
  name: string;
  rank: number;
  metrics: {
    averageScore: MetricCell;
    languageQuality: MetricCell;
    contentQuality: MetricCell;
    trustScore: MetricCell;
    localRelevance: MetricCell;
    glicko2Rating: MetricCell;
  };
};

interface LeaderboardProps {
  onOpenReport?: () => void;
}

const LANGUAGE_CODE_MAP: Record<string, string> = {
  AS: 'assamese',
  BN: 'bengali',
  EN: 'english',
  GU: 'gujarati',
  HI: 'hindi',
  KN: 'kannada',
  ML: 'malayalam',
  MR: 'marathi',
  PA: 'punjabi',
  TA: 'tamil',
  TE: 'telugu',
};

const LANGUAGE_LABELS: Record<string, string> = {
  assamese: 'Assamese',
  bengali: 'Bengali',
  english: 'English',
  gujarati: 'Gujarati',
  hindi: 'Hindi',
  kannada: 'Kannada',
  malayalam: 'Malayalam',
  marathi: 'Marathi',
  punjabi: 'Punjabi',
  tamil: 'Tamil',
  telugu: 'Telugu',
};

const MODEL_NAME_MAP: Record<string, string> = {
  GPT5: 'GPT-5',
  'Qwen3-235B-A22B-Instruct-2507': 'Qwen3-235B-A22B-Instruct-2507',
  'Kimi-K2-Instruct-0905': 'Kimi-K2-Instruct-0905',
  'gpt-oss-120b': 'GPT-OSS-120B',
  'Qwen3-Next-80B-A3B-Instruct': 'Qwen3-Next-80B-A3B-Instruct',
  'Qwen3-Next-80B-A3B-Thinking': 'Qwen3-Next-80B-A3B-Thinking',
  'Llama-4-Maverick-17B-128E-Instruct': 'Llama-4-Maverick-17B-128E-Instruct',
  'Llama-4-Scout-17B-16E-Instruct': 'Llama-4-Scout-17B-16E-Instruct',
  'Krutrim-2-instruct': 'Krutrim-2-Instruct',
  'Param-1-2.9B-Instruct': 'Param-1-2.9B-Instruct',
  'Llama-3-Nanda-10B-Chat': 'Llama-3-Nanda-10B-Chat',
  'aya-expanse-32b': 'Aya-Expanse-32B',
  'phi-4': 'Phi-4',
  Gemma3_27B_instruct: 'Gemma-3-27B-IT',
  'gemma-3-27b-it': 'Gemma-3-27B-IT',
  SarvamM_24B: 'Sarvam-M',
  'sarvam-m': 'Sarvam-M',
  QWEN3_235B_A22B: 'Qwen3-235B-A22B',
  'Qwen3-235B-A22B': 'Qwen3-235B-A22B',
  'Llama_3.1_405B_Instruct': 'Llama-3.1-405B-Instruct',
  'Llama-3.1-405B-Instruct': 'Llama-3.1-405B-Instruct',
};

const METRIC_COLUMNS: Array<{ id: SortableMetric; label: string; sortable: boolean }> = [
  { id: 'rank', label: 'Rank', sortable: false },
  { id: 'averageScore', label: 'Average score', sortable: true },
  { id: 'languageQuality', label: 'Language quality', sortable: true },
  { id: 'contentQuality', label: 'Content quality', sortable: true },
  { id: 'trustScore', label: 'Trust score', sortable: true },
  { id: 'localRelevance', label: 'Local relevance', sortable: true },
  { id: 'glicko2Rating', label: 'Elo rating', sortable: true },
];

const TABLE_COLUMN_WIDTHS = {
  rank: '6%',
  model: '18%',
  metric: '12.67%',
} as const;

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const toDomainLanguageData = (source: Record<string, RawModel[]>): Record<string, Record<string, RawModel[]>> => {
  const output: Record<string, Record<string, RawModel[]>> = {};

  Object.entries(source).forEach(([key, value]) => {
    const [langPart, domainPart] = key.split('_');
    const domainKey = domainPart === 'ALL' ? 'all' : domainPart.toLowerCase();
    const languageKey = langPart === 'ALL' ? 'all' : LANGUAGE_CODE_MAP[langPart] ?? 'all';

    if (!output[domainKey]) output[domainKey] = {};
    output[domainKey][languageKey] = value;
  });

  return output;
};

const llmRawData = llmJudgeLeaderboardData as Record<string, RawModel[]>;
const humanRawData = humanEvalLeaderboardData as Record<string, RawModel[]>;
const llmByDomainLanguage = toDomainLanguageData(llmRawData);
const humanByDomainLanguage = toDomainLanguageData(humanRawData);

const allDomainKeys = new Set<string>(['all']);
const allLanguageKeys = new Set<string>(['all']);

Object.keys(llmRawData).forEach((key) => {
  const [langPart, domainPart] = key.split('_');
  if (domainPart && domainPart !== 'ALL') allDomainKeys.add(domainPart.toLowerCase());
  if (langPart && langPart !== 'ALL' && LANGUAGE_CODE_MAP[langPart]) {
    allLanguageKeys.add(LANGUAGE_CODE_MAP[langPart]);
  }
});

const preferredDomainOrder = ['all', 'education', 'healthcare', 'finance', 'legal'];
const domains = [
  ...preferredDomainOrder.filter((d) => allDomainKeys.has(d)),
  ...Array.from(allDomainKeys).filter((d) => !preferredDomainOrder.includes(d)).sort(),
].map((id) => ({
  id,
  name: id === 'all' ? 'All domains' : `${id.charAt(0).toUpperCase()}${id.slice(1)}`,
}));

const languages = [
  { id: 'all', name: 'All languages' },
  ...Array.from(allLanguageKeys)
    .filter((lang) => lang !== 'all')
    .sort()
    .map((id) => ({ id, name: LANGUAGE_LABELS[id] ?? `${id.charAt(0).toUpperCase()}${id.slice(1)}` })),
];

const mapRows = (models: RawModel[]): LeaderboardRow[] => {
  return models.map((model) => {
    const lq = model.metrics.languageQuality;
    const cq = model.metrics.contentQuality;
    const ts = model.metrics.trustworthiness;
    const lr = model.metrics.localRelevance;
    const averageValue = (lq.value + cq.value + ts.value + lr.value) / 4;
    const averageError = Math.sqrt(lq.error ** 2 + cq.error ** 2 + ts.error ** 2 + lr.error ** 2) / 4;

    return {
      id: model.id,
      name: getDisplayModelName(model.name),
      rank: model.rank,
      metrics: {
        averageScore: { value: averageValue, error: averageError },
        languageQuality: { value: lq.value, error: lq.error },
        contentQuality: { value: cq.value, error: cq.error },
        trustScore: { value: ts.value, error: ts.error },
        localRelevance: { value: lr.value, error: lr.error },
        glicko2Rating: { value: model.rank, error: null },
      },
    };
  });
};

const formatCellValue = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return '--';
  return `${roundToTwoDecimals(value)}`;
};

const formatGlicko = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return '--';
  return `${Math.round(value)}`;
};

const getDisplayModelName = (modelName: string): string => MODEL_NAME_MAP[modelName] ?? modelName;

const Leaderboard: React.FC<LeaderboardProps> = ({ onOpenReport }) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('all');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [selectedEvaluationType, setSelectedEvaluationType] = useState<EvaluationType>('human');
  const [sortedMetric, setSortedMetric] = useState<SortableMetric | null>('averageScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const currentRows = useMemo(() => {
    const source = selectedEvaluationType === 'human' ? humanByDomainLanguage : llmByDomainLanguage;
    const raw = source[selectedDomain]?.[selectedLanguage] ?? [];
    return mapRows(raw);
  }, [selectedDomain, selectedLanguage, selectedEvaluationType]);

  const sortedRows = useMemo(() => {
    if (!sortedMetric || !sortDirection) return currentRows;

    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return [...currentRows].sort((a, b) => {
      let aValue = 0;
      let bValue = 0;

      if (sortedMetric === 'rank') {
        aValue = a.rank;
        bValue = b.rank;
      } else if (sortedMetric === 'glicko2Rating') {
        aValue = a.metrics.glicko2Rating.value ?? 0;
        bValue = b.metrics.glicko2Rating.value ?? 0;
      } else {
        aValue = a.metrics[sortedMetric].value ?? 0;
        bValue = b.metrics[sortedMetric].value ?? 0;
      }

      return (aValue - bValue) * multiplier;
    });
  }, [currentRows, sortedMetric, sortDirection]);

  const onSort = (metric: SortableMetric) => {
    if (sortedMetric === metric) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortedMetric(null);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
      return;
    }
    setSortedMetric(metric);
    setSortDirection('asc');
  };

  React.useEffect(() => {
    setSortedMetric('averageScore');
    setSortDirection('desc');
  }, [selectedDomain, selectedLanguage, selectedEvaluationType]);

  const renderSortIcon = (metric: SortableMetric) => {
    const isActive = sortedMetric === metric;
    if (!isActive || !sortDirection) return <Icon as={LuArrowUpDown} fontSize="16px" color="#737D7F" />;
    if (sortDirection === 'asc') return <Icon as={LuArrowUp} fontSize="16px" color="#002029" />;
    return <Icon as={LuArrowDown} fontSize="16px" color="#002029" />;
  };

  return (
    <Box id="leaderboard" w="100%" py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }} style={{ scrollMarginTop: '100px' }}>
      <VStack gap={{ base: 6, md: 11 }} alignItems="center" w="100%" maxW="1440px" mx="auto">
        <Text
          fontFamily="heading"
          fontSize={{ base: '32px', sm: '40px', md: '48px', lg: '72px' }}
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-3px"
          color="white"
          textAlign="center"
        >
          Leaderboard
        </Text>

        <HStack gap={4} alignItems="stretch" justifyContent="center" w="100%" flexDirection={{ base: 'column', sm: 'row' }} flexWrap="wrap">
          <Dropdown
            options={domains.map((d) => ({
              id: d.id,
              label: d.name,
              hasCheckbox: d.id === 'all',
              isFirstItemWithBorder: d.id === 'all',
            }))}
            selectedValue={selectedDomain}
            onSelect={setSelectedDomain}
            placeholder="All domains"
            width={{ base: '100%', sm: '304px', md: '304px' }}
          />
          <Dropdown
            options={languages.map((l) => ({
              id: l.id,
              label: l.name,
              hasCheckbox: l.id === 'all',
              isFirstItemWithBorder: l.id === 'all',
            }))}
            selectedValue={selectedLanguage}
            onSelect={setSelectedLanguage}
            placeholder="All languages"
            width={{ base: '100%', sm: '304px', md: '304px' }}
          />
        </HStack>

        <Box w="100%" maxW="1182px" borderRadius="4px" overflow="hidden">
          <HStack gap={0} w="100%" h="61px">
            <Box
              as="button"
              w="50%"
              h="61px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="12px"
              bg={selectedEvaluationType === 'human' ? '#055369' : '#E3F9FF'}
              border="none"
              onClick={() => setSelectedEvaluationType('human')}
              aria-pressed={selectedEvaluationType === 'human'}
            >
              <Icon as={selectedEvaluationType === 'human' ? FaCheckCircle : MdOutlineRadioButtonUnchecked} fontSize="16px" color={selectedEvaluationType === 'human' ? 'white' : '#002029'} />
              <Text fontFamily="body" fontSize="14px" fontWeight="600" lineHeight="21px" color={selectedEvaluationType === 'human' ? 'white' : '#002029'}>
                Human Evaluation
              </Text>
            </Box>
            <Box
              as="button"
              w="50%"
              h="61px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="12px"
              bg={selectedEvaluationType === 'llm' ? '#055369' : '#E3F9FF'}
              border="none"
              onClick={() => setSelectedEvaluationType('llm')}
              aria-pressed={selectedEvaluationType === 'llm'}
            >
              <Icon as={selectedEvaluationType === 'llm' ? FaCheckCircle : MdOutlineRadioButtonUnchecked} fontSize="16px" color={selectedEvaluationType === 'llm' ? 'white' : '#002029'} />
              <Text fontFamily="body" fontSize="14px" fontWeight="600" lineHeight="21px" color={selectedEvaluationType === 'llm' ? 'white' : '#002029'}>
                LLM-as-a-judge Evaluations
              </Text>
            </Box>
          </HStack>

          <Box overflowX={{ base: 'auto', xl: 'hidden' }} w="100%">
            <Table.Root variant="outline" size="sm" w="100%" tableLayout="fixed" minW={{ base: '980px', xl: '100%' }}>
              <Table.Header>
                <Table.Row h="74px" borderBottom="1px solid rgba(0,0,0,0.1)" bg="white">
                  <Table.ColumnHeader w={TABLE_COLUMN_WIDTHS.rank} px="8px" py="16px">
                    <Text fontFamily="body" fontSize="14px" fontWeight="600" lineHeight="21px" color="#002029" textAlign="center">
                      Rank
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader w={TABLE_COLUMN_WIDTHS.model} px="8px" py="16px">
                    <Text fontFamily="body" fontSize="14px" fontWeight="600" lineHeight="21px" color="#002029" textAlign="center">
                      Model
                    </Text>
                  </Table.ColumnHeader>
                  {METRIC_COLUMNS.filter((column) => column.id !== 'rank').map((column) => (
                    <Table.ColumnHeader key={column.id} w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="16px">
                      <HStack gap="8px" justifyContent="flex-start">
                        {column.sortable && (
                          <IconButton
                            variant="ghost"
                            size="xs"
                            minW="24px"
                            w="24px"
                            h="24px"
                            p="0"
                            aria-label={`Sort by ${column.label}`}
                            onClick={() => onSort(column.id)}
                          >
                            {renderSortIcon(column.id)}
                          </IconButton>
                        )}
                        <Text fontFamily="body" fontSize="14px" fontWeight="600" lineHeight="21px" color="#002029">
                          {column.label}
                        </Text>
                      </HStack>
                    </Table.ColumnHeader>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedRows.map((row, idx) => (
                  <Table.Row key={row.id} h="82px" borderBottom="1px solid rgba(0,0,0,0.1)" bg="white">
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.rank} px="8px" py="20px">
                      <Text fontFamily="body" fontSize="14px" fontWeight="600" lineHeight="21px" color="#002029" textAlign="center">
                        {idx + 1}
                      </Text>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.model} px="8px" py="20px">
                      <Text
                        fontFamily="body"
                        fontSize="14px"
                        fontWeight="600"
                        lineHeight="21px"
                        color="#002029"
                        textAlign="center"
                        lineClamp={2}
                        wordBreak="break-word"
                      >
                        {row.name}
                      </Text>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="20px">
                      <HStack gap="4px" justifyContent="center">
                        <Text fontFamily="body" fontSize="14px" fontWeight="700" lineHeight="21px" color="#002029">
                          {formatCellValue(row.metrics.averageScore.value)}
                        </Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="20px">
                      <HStack gap="4px" justifyContent="center">
                        <Text fontFamily="body" fontSize="14px" fontWeight="700" lineHeight="21px" color="#002029">
                          {formatCellValue(row.metrics.languageQuality.value)}
                        </Text>
                        <Text fontFamily="body" fontSize="14px" fontWeight="500" lineHeight="21px" color="#737D7F">
                          ({`±${roundToTwoDecimals(row.metrics.languageQuality.error ?? 0)}`})
                        </Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="20px">
                      <HStack gap="4px" justifyContent="center">
                        <Text fontFamily="body" fontSize="14px" fontWeight="700" lineHeight="21px" color="#002029">
                          {formatCellValue(row.metrics.contentQuality.value)}
                        </Text>
                        <Text fontFamily="body" fontSize="14px" fontWeight="500" lineHeight="21px" color="#737D7F">
                          ({`±${roundToTwoDecimals(row.metrics.contentQuality.error ?? 0)}`})
                        </Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="20px">
                      <HStack gap="4px" justifyContent="center">
                        <Text fontFamily="body" fontSize="14px" fontWeight="700" lineHeight="21px" color="#002029">
                          {formatCellValue(row.metrics.trustScore.value)}
                        </Text>
                        <Text fontFamily="body" fontSize="14px" fontWeight="500" lineHeight="21px" color="#737D7F">
                          ({`±${roundToTwoDecimals(row.metrics.trustScore.error ?? 0)}`})
                        </Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="20px">
                      <HStack gap="4px" justifyContent="center">
                        <Text fontFamily="body" fontSize="14px" fontWeight="700" lineHeight="21px" color="#002029">
                          {formatCellValue(row.metrics.localRelevance.value)}
                        </Text>
                        <Text fontFamily="body" fontSize="14px" fontWeight="500" lineHeight="21px" color="#737D7F">
                          ({`±${roundToTwoDecimals(row.metrics.localRelevance.error ?? 0)}`})
                        </Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell w={TABLE_COLUMN_WIDTHS.metric} px="8px" py="20px">
                      <Text fontFamily="body" fontSize="14px" fontWeight="700" lineHeight="21px" color="#002029" textAlign="center">
                        {formatGlicko(row.metrics.glicko2Rating.value)}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Box>
        <Text fontFamily="body" fontSize="16px" fontWeight="normal" lineHeight="1.6" color="#b7b7b7" textAlign="center" fontStyle="italic" mt={{ base: -4, md: -6 }}>
          Elo ratings: higher is better. All other scores are on a scale of 1 (lowest) to 3 (highest), with standard deviation reported alongside. <br />
        </Text>
        <Text fontFamily="body" fontSize="16px" fontWeight="normal" lineHeight="1.6" color="#b7b7b7" textAlign="center" fontStyle="italic" mt={{ base: -4, md: -6 }}>
          Sample sizes vary across languages and domains; refer to the{' '}
          <Link
            href="#"
            display="inline-flex"
            alignItems="center"
            gap="4px"
            color="#b7b7b7"
            textDecoration="underline"
            _hover={{ opacity: 0.8 }}
            onClick={(event) => {
              event.preventDefault();
              onOpenReport?.();
            }}
          >
            full report
            <Icon as={MdOutlineOpenInNew} boxSize="14px" />
          </Link>{' '}
          for detailed breakdowns.
        </Text>
      </VStack>
    </Box>
  );
};

export default Leaderboard;
